import React, { useContext, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { usePlace } from "../context/PlaceContext";
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Menu, 
  MenuItem, 
  Button, 
  Divider, 
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { 
  Menu as MenuIcon,
  Business, 
  ExitToApp,
  LocationOn 
} from "@mui/icons-material";
import { getPlacesByUserName } from "../services/userService";
const comhubLogo = "/LOGO-COMHUB-02.png";

const Navbar = ({ toggleMenu }) => {
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const { selectedPlace, changePlace } = usePlace();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdminUser = user?.is_admin;
  const [anchorEl, setAnchorEl] = useState(null);
  const [placesAnchorEl, setPlacesAnchorEl] = useState(null);
  const [places, setPlaces] = useState([]);
  const NAME_ORGANIZATION = JSON.parse(sessionStorage.getItem("organization"));
  const USERNAME = sessionStorage.getItem("username");
  const navigate = useNavigate();

  if (location.pathname === "/login") {
    return null;
  }

  const handlePlacesClick = (event) => {
    setPlacesAnchorEl(event.currentTarget);
    if (places.length === 0) {
      loadPlaces();
    }
  };

  const loadPlaces = async () => {
    if (places.length === 0) {
      try {
        const response = await getPlacesByUserName();
        sessionStorage.setItem("userId",  response.data[0].id);
        setPlaces(response.data[0].places);
      } catch (error) {
        console.error("Error loading places", error);
      }
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPlacesAnchorEl(null);
  };

  const handleMenuPlaceSelected = (place) => {
    changePlace(place);
    handleMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleToggle = () => {
    if (typeof toggleMenu === 'function') {
      toggleMenu();
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: 1300, // Mayor que el zIndex del Drawer (1200)
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
        {/* Botón hamburguesa */}
        <IconButton
          edge="start"
          onClick={handleToggle}
          sx={{
            mr: 2,
            color: '#0B2039',
            backgroundColor: 'rgba(11, 32, 57, 0.08)',
            border: '1px solid rgba(11, 32, 57, 0.12)',
            borderRadius: '12px',
            width: { xs: '44px', sm: '48px' },
            height: { xs: '44px', sm: '48px' },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(11, 32, 57, 0.12)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(11, 32, 57, 0.15)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
        </IconButton>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NavLink to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <Box
              component="img"
              src={comhubLogo}
              alt="ComHub Logo"
              sx={{
                height: { xs: '32px', sm: '40px' },
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            />
          </NavLink>
        </Box>

        {/* Selector de ubicación - Solo en desktop */}
        {!isMobile && (
          <Button
            startIcon={<LocationOn />}
            onClick={handlePlacesClick}
            sx={{
              mr: 2,
              color: '#0B2039',
              backgroundColor: 'rgba(50, 105, 144, 0.08)',
              border: '1px solid rgba(50, 105, 144, 0.12)',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 1,
              maxWidth: '200px',
              '&:hover': {
                backgroundColor: 'rgba(50, 105, 144, 0.12)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.85rem',
              }}
            >
              {selectedPlace
                ? selectedPlace.name
                : isAdminUser
                ? "Todas las sedes"
                : "No hay sedes disponibles"}
            </Typography>
          </Button>
        )}

        {/* Menú de ubicaciones */}
        <Menu
          anchorEl={placesAnchorEl}
          open={Boolean(placesAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(0,0,0,0.06)',
              mt: 1,
              minWidth: '250px',
            }
          }}
        >
          {places.length > 0 ? (
            places.map((place) => (
              <MenuItem
                key={place.id}
                onClick={() => handleMenuPlaceSelected(place)}
                sx={{
                  borderRadius: '8px',
                  mx: 1,
                  my: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(50, 105, 144, 0.08)',
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {place.name_place}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {place.address}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay sedes disponibles</MenuItem>
          )}
          {isAdminUser && (
            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem
                onClick={() => {
                  changePlace(null);
                  handleMenuClose();
                }}
                sx={{
                  borderRadius: '8px',
                  mx: 1,
                  mb: 0.5,
                  color: '#FF9800',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  },
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Todas las sedes
                </Typography>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Botón de logout */}
        <IconButton
          onClick={handleLogout}
          sx={{
            color: '#0B2039',
            backgroundColor: 'rgba(244, 67, 54, 0.08)',
            border: '1px solid rgba(244, 67, 54, 0.12)',
            borderRadius: '12px',
            width: { xs: '44px', sm: '48px' },
            height: { xs: '44px', sm: '48px' },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.12)',
              color: '#f44336',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
            },
          }}
        >
          <ExitToApp sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
